/**
 * Literal JS port of `Invitation#out_at_time?` / `is_out_of_prev_time?` /
 * `is_out_of_post_time?` (see `CorporateService/app/models/invitation.rb`,
 * lines 24-42) — the rule the backend applies in `GET /invitations/search`
 * (and `GET /invitations/:code`, the QR endpoint) to decide whether an
 * invitation is outside its allowed check-in window.
 *
 * This is intentionally a line-by-line translation (including branches that
 * look redundant) rather than a "cleaned up" reformulation: reimplementing
 * from first principles risks silently diverging from the real production
 * behavior. If `Invitation#out_at_time?` ever changes on the backend, this
 * function must be updated to match.
 *
 * Returns the full breakdown (not just the combined boolean) because callers
 * like the offline QR flow (`onSearchByQRInvitations` in `VisitForm.js`) need
 * to distinguish `isOutOfPrevTime` (invitation not started yet → "too early"
 * modal) from `isOutOfPostTime` (invitation window already closed →
 * "expired" modal), mirroring `is_out_of_prev_time`/`is_out_of_post_time` in
 * the `GET /invitations/:code` response.
 *
 * `outOfTime` keeps using the exact (redundant-branches-included) formula
 * ported below, so it stays byte-for-byte identical to the previous
 * `isInvitationOutOfTime` for every existing caller. `isOutOfPrevTime`,
 * however, is reported WITHOUT that redundant branch — the literal formula
 * also flags `isOutOfPrevTime` once the window has been closed for a while
 * (since `now - start` naturally exceeds `prevMinutes` once `duration` has
 * elapsed too), which would make the QR flow show the "too early" modal for
 * an invitation that's actually long expired. Dropping that branch keeps
 * `isOutOfPrevTime`/`isOutOfPostTime` mutually exclusive for modal selection
 * while `outOfTime` (the only thing the pre-existing callers rely on) is
 * unaffected, since it's still `true` via the post-time branch in that case.
 *
 * @param {object} invitation Dexie `invitations` record — must carry the
 *   fields added to `Invitation#to_json` for this purpose: `invitation_datetime`,
 *   `duration`, `qr_validation_enabled`, `allowed_invitation_qr_validity_prev_time`,
 *   `allowed_invitation_qr_validity_post_time`.
 * @param {Date} [now] Injectable for tests; defaults to the current time.
 * @returns {{outOfTime: boolean, isOutOfPrevTime: boolean, isOutOfPostTime: boolean}}
 *   All `false` when the company has QR validity disabled
 *   (`qr_validation_enabled` falsy) — matching `enabled_qr_validation?`
 *   short-circuiting `out_at_time?` to `false`.
 */
export function getInvitationTimeStatus(invitation, now = new Date()) {
  const hasBackendPrevStatus = typeof invitation?.is_out_of_prev_time === 'boolean'
  const hasBackendPostStatus = typeof invitation?.is_out_of_post_time === 'boolean'

  if (hasBackendPrevStatus || hasBackendPostStatus) {
    const isOutOfPrevTime = !!invitation.is_out_of_prev_time
    const isOutOfPostTime = !!invitation.is_out_of_post_time
    return {
      outOfTime: typeof invitation.out_of_time === 'boolean'
        ? invitation.out_of_time
        : isOutOfPrevTime || isOutOfPostTime,
      isOutOfPrevTime,
      isOutOfPostTime,
    }
  }

  if (!invitation?.qr_validation_enabled) {
    return { outOfTime: false, isOutOfPrevTime: false, isOutOfPostTime: false }
  }

  const start = new Date(invitation.invitation_datetime)
  const end = new Date(start.getTime() + (invitation.duration || 0) * 60000)
  const prevMinutes = invitation.allowed_invitation_qr_validity_prev_time || 0
  const postMinutes = invitation.allowed_invitation_qr_validity_post_time || 0
  const minutesBetween = (a, b) => Math.floor((a - b) / 60000)

  const isOutOfPrevTimeRaw =
    (now > start && end < now && minutesBetween(now, start) > prevMinutes) ||
    (start > now && minutesBetween(start, now) > prevMinutes)
  const isOutOfPostTime = end < now && minutesBetween(now, end) > postMinutes
  const isOutOfPrevTime = start > now && minutesBetween(start, now) > prevMinutes
  return { outOfTime: isOutOfPrevTimeRaw || isOutOfPostTime, isOutOfPrevTime, isOutOfPostTime }
}

/**
 * Convenience wrapper over `getInvitationTimeStatus` for callers that only
 * need the combined boolean (e.g. the local identification search in
 * `onSearchByInvitations`).
 *
 * @param {object} invitation
 * @param {Date}   [now]
 * @returns {boolean}
 */
export function isInvitationOutOfTime(invitation, now = new Date()) {
  return getInvitationTimeStatus(invitation, now).outOfTime
}

/**
 * Whether the QR flow should show the expired modal (`openExpiredQRModal`).
 *
 * True whenever the invitation is past its validity window — not only when
 * `isOutOfPostTime` is set. After the scheduled end, the backend's
 * `out_at_time?` is already true via the redundant prev-time branch, but
 * `isOutOfPostTime` stays false until the post-time grace elapses. Treating
 * any non-early `outOfTime` as expired guarantees the modal always fires for a
 * vencido QR while allowing multi-day reservations whose `invitation_date` is
 * the range start date.
 *
 * @param {object} invitation
 * @param {Date}   [now]
 * @returns {boolean}
 */
export function isQrExpired(invitation, now = new Date()) {
  if (!invitation) return false

  const { outOfTime, isOutOfPrevTime, isOutOfPostTime } = getInvitationTimeStatus(invitation, now)
  if (isOutOfPostTime) return true
  if (outOfTime && !isOutOfPrevTime) return true

  return false
}

/**
 * Reconstructs `invitation_datetime` for a locally-created invitation that
 * never went through `Invitation#to_json`. Local writes store the scheduled
 * start as `invitation_time` (ISO-like `yyyy-MM-dd'T'HH:mm:ss.SSS` from
 * `formatDateAndTimeToISO`, or a time-only string) plus `invitation_date` /
 * `date` (`YYYY-MM-DD`).
 *
 * @param {object} invitation
 * @returns {string|null}
 */
export function deriveInvitationDatetime(invitation) {
  if (!invitation) return null
  if (invitation.invitation_datetime) return invitation.invitation_datetime

  const time = invitation.invitation_time
  if (time && String(time).includes('T')) return String(time)

  const day = invitation.invitation_date || invitation.date
  if (day && time) {
    const dayPart = String(day).split('T')[0]
    const timePart = String(time)
    return `${dayPart}T${timePart}`
  }

  return time ? String(time) : null
}

/**
 * Stamps the vigencia fields that `Invitation#to_json` adds for offline QR
 * validation onto an invitation record.
 *
 * When `companies` is provided and the invitation's company is found, always
 * recompute from that company (invitation.company_id / destination.company_id).
 * That matches CorporateService `Invitation#qr_validity_company` and corrects
 * stale Dexie rows where `qr_validation_enabled` was false because the
 * receptionist host has no `user.company`.
 *
 * If no matching company is available, keeps any server-stamped fields.
 *
 * @param {object} invitation
 * @param {Array<{id: number, enabled_invitation_qr_validity?: boolean, allowed_invitation_qr_validity_prev_time?: number, allowed_invitation_qr_validity_post_time?: number}>} [companies]
 * @returns {object}
 */
export function attachInvitationQrValidity(invitation, companies = []) {
  if (!invitation) return invitation

  const next = { ...invitation }

  if (!next.invitation_datetime) {
    next.invitation_datetime = deriveInvitationDatetime(next)
  }

  const companyId = next.destination?.company_id ?? next.company_id
  const company = (companies || []).find(c => Number(c?.id) === Number(companyId))
  if (!company) return next

  const prev = Number(company.allowed_invitation_qr_validity_prev_time) || 0
  const post = Number(company.allowed_invitation_qr_validity_post_time) || 0
  const enabled = !!(company.enabled_invitation_qr_validity && prev > 0 && post > 0)

  next.qr_validation_enabled = enabled
  next.allowed_invitation_qr_validity_prev_time = enabled ? prev : null
  next.allowed_invitation_qr_validity_post_time = enabled ? post : null

  return next
}
